// Cabinet/Cabinet.tsx
import Chat from "Chat/Chat";
import Room from "Chat/Room";
import Profile from "User/Profile";
import Menu from '@mui/material/Menu';
import pprint from "../../Utils/pprint";
import GuidePage from "Guide/GuidePage";
import GuidesPage from "Guide/GuidesPage";
import GuideOrder from "Guide/GuideOrder";
import UserOrders from "Order/UserOrders";
import CoursePage from "Course/CoursePage";
import TariffPage from "Tariff/TariffPage";
import {useTheme} from "Theme/ThemeContext";
import {useMediaQuery} from "@mui/material";
import OrderDetail from "Order/OrderDetail";
import CoursesPage from "Course/CoursesPage";
import TariffOrder from "Tariff/TariffOrder";
import CourseOrder from "Course/CourseOrder";
import CabinetNavLink from "./CabinetNavLink";
import MenuItem from '@mui/material/MenuItem';
import ClientDetail from "Client/ClientDetail";
import CalendarApp from "Employee/CalendarApp";
import {useProfile} from "User/ProfileContext";
import OrderTemplate from "Order/OrderTemplate";
import {RoomsProvider} from "Chat/RoomsContext";
import ChatIcon from "@mui/icons-material/Chat";
import TariffsPage from "Tariff/TariffGroupsPage";
import ConsultationList from "./ConsultationList";
import ClientsWrapper from "Client/ClientsWrapper";
import CabinetBaseHeader from "./CabinetBaseHeader";
import SchoolIcon from "@mui/icons-material/School";
import PeopleIcon from "@mui/icons-material/People";
import NavLink from "Core/components/Header/NavLink";
import Psychologists from "Psychologist/Psychologists";
import {FC, FCSC, FRC, FRCC} from "WideLayout/Layouts";
import ScheduleIcon from "@mui/icons-material/Schedule";
import Modal from "Core/components/elements/Modal/Modal";
import UserAvatarEditable from "User/UserAvatarEditable";
import DateRangeIcon from "@mui/icons-material/DateRange";
import {headerHeight} from "Core/components/Header/Header";
import Questionnaire from "../Questionnaire/Questionnaire";
import Button from "Core/components/elements/Button/Button";
import {AuthContext, AuthContextType} from "Auth/AuthContext";
import LoopRoundedIcon from '@mui/icons-material/LoopRounded';
import PackageOrder from "../ConsultationPackage/PackageOrder";
import ConsultationDetail from "Consultation/ConsultationDetail";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import {useNavigation} from "Core/components/Header/HeaderProvider";
import React, {useContext, useEffect, useRef, useState} from 'react';
import GiftCertificatePage from "GiftCertificate/GiftCertificatePage";
import GiftCertificatesPage from "GiftCertificate/GiftCertificatesPage";
import GiftCertificateOrder from "GiftCertificate/GiftCertificateOrder";
import ConsultationConference from "Consultation/ConsultationConference";
import PsychologistDetailPage from "Psychologist/PsychologistDetailPage";
import DonutSmallRoundedIcon from '@mui/icons-material/DonutSmallRounded';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import FavoritePsychologists from "Psychologist/Favorites/FavoritesPsychologists";
import {Navigate, Route, Routes, useLocation, useNavigate} from "react-router-dom";
import StackedLineChartRoundedIcon from '@mui/icons-material/StackedLineChartRounded';
import GiftCertificateActivationPage from "GiftCertificate/GiftCertificateActivationPage";
import TariffGroupDetailPage from "Tariff/TariffGroupDetailPage";

type CabinetWidthContextType = {
    cabinetMaxWidth: string;
    setCabinetMaxWidth: (w: string) => void;
};

const CabinetWidthContext = React.createContext<CabinetWidthContextType>({
    cabinetMaxWidth: "1220px",
    setCabinetMaxWidth: () => {
    },
});

export const useCabinetWidth = () => useContext(CabinetWidthContext);
// =========================================================

const Cabinet: React.FC = () => {
    const {isAuthenticated, user} = useContext(AuthContext) as AuthContextType;
    const {setMobileNavigationContent, hideMobileMenu} = useNavigation();
    const {selectedProfile, switchProfile} = useProfile();
    const isLargeScreen = useMediaQuery('(min-width: 576px)');
    const cabinetContainerRef = useRef<HTMLDivElement>(null);
    const [anchorElExtra, setAnchorElExtra] = useState<null | HTMLElement>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const {plt} = useTheme();
    const isGtSm = useMediaQuery('(min-width: 576px)');

    const [cabinetMaxWidth, setCabinetMaxWidth] = useState<string>("1220px");

    const [isLeaveConferenceModalOpen, setIsLeaveConferenceModalOpen] = useState(false);
    const [pendingPath, setPendingPath] = useState<string | null>(null);

    const handleMenuLinkClick = (path: string, closeMobile?: boolean) => {
        const isInConference = location.pathname.includes('/conference');
        if (isInConference) {
            pprint(`Confirm leave from conference -> ${path}`);
            setPendingPath(path);
            setIsLeaveConferenceModalOpen(true);
        } else {
            if (closeMobile) hideMobileMenu();
            setTimeout(() => {
                navigate(path);
                pprint(`Cabinet navigate -> ${path}`);
            }, 0);
        }
    };

    // ***** "Ещё..." *****
    const handleExtraClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElExtra(event.currentTarget);
    };

    const handleExtraClose = () => {
        setAnchorElExtra(null);
    };

    const confirmLeaveConference = () => {
        setIsLeaveConferenceModalOpen(false);
        if (pendingPath) {
            navigate(
                `${pendingPath}?for_feedback=${
                    window.location.href.split('/')[window.location.href.split('/').length - 1]
                }`
            );
        }
        setPendingPath(null);
    };
    const cancelLeaveConference = () => {
        setIsLeaveConferenceModalOpen(false);
        setPendingPath(null);
    };

    useEffect(() => {
        if (!isLargeScreen) {
            setMobileNavigationContent(<>
                {user?.psychologist && <FRCC g={1} onClick={switchProfile}>
                    <LoopRoundedIcon/>
                    <span style={{lineHeight: '1rem', marginTop: 2}}>
                        Я сейчас {selectedProfile === 'psychologist' ? 'психолог' : 'клиент'}
                    </span>
                </FRCC>}
                {selectedProfile === 'psychologist' && <>
                    <NavLink onClick={() => handleMenuLinkClick('/consultations', true)}
                             to="/consultations">
                        Консультации
                    </NavLink>
                    <NavLink onClick={() => handleMenuLinkClick('/profile', true)}
                             to="/profile" icon={<AssignmentIndIcon/>}>
                        Профиль
                    </NavLink>
                    <NavLink onClick={() => handleMenuLinkClick('/chats/', true)}
                             to="/chats/" icon={<ChatIcon/>}>
                        Чаты
                    </NavLink>
                    <NavLink onClick={() => handleMenuLinkClick('/clients', true)}
                             to="/clients" icon={<PeopleIcon/>}>
                        Клиенты
                    </NavLink>
                    <NavLink onClick={() => handleMenuLinkClick('/work-schedule', true)}
                             to="/work-schedule" icon={<ScheduleIcon/>}>
                        График работы
                    </NavLink>
                    <NavLink onClick={() => handleMenuLinkClick('/payouts', true)}
                             to="/payouts" icon={<AccountBalanceWalletIcon/>}>
                        Выплаты
                    </NavLink>
                </>}

                {selectedProfile === 'client' && <>
                    <NavLink onClick={() => handleMenuLinkClick('/consultations', true)}
                             to="/consultations" icon={<DateRangeIcon/>}>
                        Консультации
                    </NavLink>
                    <NavLink onClick={() => handleMenuLinkClick('/profile', true)}
                             to="/profile" icon={<AssignmentIndIcon/>}>
                        Профиль
                    </NavLink>
                    <NavLink onClick={() => handleMenuLinkClick('/chats/', true)}
                             to="/chats/" icon={<ChatIcon/>}>
                        Чаты
                    </NavLink>
                    <NavLink onClick={() => handleMenuLinkClick('/psychologists', true)}
                             to="/psychologists" icon={<PeopleIcon/>}>
                        Психологи
                    </NavLink>
                    <NavLink onClick={() => handleMenuLinkClick('/courses', true)}
                             to="/courses" icon={<SchoolIcon/>}>
                        Курсы
                    </NavLink>
                    <NavLink onClick={() => handleMenuLinkClick('/guides', true)}
                             to="/guides" icon={<StackedLineChartRoundedIcon/>}>
                        Гайды
                    </NavLink>
                    <NavLink onClick={() => handleMenuLinkClick('/tariffs', true)}
                             to="/tariffs" icon={<DonutSmallRoundedIcon/>}>
                        Тарифы
                    </NavLink>
                    <NavLink onClick={() => handleMenuLinkClick('/orders', true)}
                             to="/orders" icon={<AccountBalanceWalletIcon/>}>
                        Архив
                    </NavLink>
                    <FRCC onClick={handleExtraClick} cursorPointer>
                        Ещё...
                    </FRCC>
                </>}
            </>);
        }
        return () => setMobileNavigationContent(null);
    }, [setMobileNavigationContent, selectedProfile, isLargeScreen, user?.psychologist]);

    useEffect(() => {
        if (isAuthenticated === false) navigate('/auth');
        pprint('Cabinet');
    }, [isAuthenticated, navigate]);

    if (!isAuthenticated) return null;

    return (
        <CabinetWidthContext.Provider value={{cabinetMaxWidth, setCabinetMaxWidth}}>
            <FC mx={'auto'} h={'100%'} w={'100%'} maxW={cabinetMaxWidth}>
                <FRC h={'100%'} w={'100%'} scroll={'y-hidden'} maxH={`calc(100vh - ${headerHeight}px)`}>
                    <FCSC minW={'fit-content'} scroll={'y-auto'}
                          cls={'no-scrollbar d-sm-flex d-none'} p={2} g={2}
                          display={isGtSm ? 'flex' : 'none'}>
                        <FCSC g={1}>
                            <FC pos={'relative'}>
                                <UserAvatarEditable size={'8em'}/>
                            </FC>
                            <h6 className={`fw-6 fs-5 text-wrap`} style={{color: plt.text.primary70}}>
                                {user?.first_name} {user?.last_name}
                            </h6>
                        </FCSC>
                        <FC g={1}>
                            {selectedProfile === 'psychologist' && <>
                                <CabinetNavLink
                                    text={'Консультации'} to="/consultations"
                                    urlActiveMark={'consultation'} icon={DateRangeIcon}
                                    onClick={() => handleMenuLinkClick('/consultations')}/>
                                <CabinetNavLink
                                    text={'Профиль'} to="/profile" urlActiveMark={'profile'}
                                    icon={AssignmentIndIcon}
                                    onClick={() => handleMenuLinkClick('/profile')}/>
                                <CabinetNavLink
                                    text={'Чаты'} to="/chats/" urlActiveMark={'chat'} icon={ChatIcon}
                                    onClick={() => handleMenuLinkClick('/chats/')}/>
                                <CabinetNavLink
                                    text={'Клиенты'} to="/clients" urlActiveMark={'client'}
                                    icon={PeopleIcon} onClick={() => handleMenuLinkClick('/clients')}/>
                                <CabinetNavLink
                                    text={'График работы'} to="/work-schedule"
                                    urlActiveMark={'work-schedule'} icon={ScheduleIcon}
                                    onClick={() => handleMenuLinkClick('/work-schedule')}/>
                                <CabinetNavLink
                                    text={'Выплаты'} to="/payouts" urlActiveMark={'payout'}
                                    icon={AccountBalanceWalletIcon}
                                    onClick={() => handleMenuLinkClick('/payouts')}/>
                            </>}
                            {selectedProfile === 'client' && <>
                                <CabinetNavLink
                                    text={'Консультации'} to="/consultations" urlActiveMark={'consultation'}
                                    icon={DateRangeIcon} onClick={() => handleMenuLinkClick('/consultations')}/>
                                <CabinetNavLink
                                    text={'Профиль'} to="/profile" urlActiveMark={'profile'} icon={AssignmentIndIcon}
                                    onClick={() => handleMenuLinkClick('/profile')}/>
                                <CabinetNavLink
                                    text={'Чаты'} to="/chats/" urlActiveMark={'chat'} icon={ChatIcon}
                                    onClick={() => handleMenuLinkClick('/chats/')}/>
                                <CabinetNavLink
                                    text={'Психологи'} to="/psychologists" urlActiveMark={'psychologist'}
                                    icon={PeopleIcon} onClick={() => handleMenuLinkClick('/psychologists')}/>
                                <CabinetNavLink
                                    text={'Курсы'} to="/courses" urlActiveMark={'course'} icon={SchoolIcon}
                                    onClick={() => handleMenuLinkClick('/courses')}/>
                                <CabinetNavLink
                                    text={'Гайды'} to="/guides" urlActiveMark={'guide'}
                                    icon={StackedLineChartRoundedIcon} onClick={() => handleMenuLinkClick('/guides')}/>
                                <CabinetNavLink
                                    text={'Тарифы'} to="/tariffs" urlActiveMark={'tariff'} icon={DonutSmallRoundedIcon}
                                    onClick={() => handleMenuLinkClick('/tariffs')}/>
                                <CabinetNavLink
                                    text={'Архив'} to="/orders" urlActiveMark={'order'} icon={AccountBalanceWalletIcon}
                                    onClick={() => handleMenuLinkClick('/orders')}/>
                            </>}
                            <FRCC ml={2.3} mr={'auto'} onClick={handleExtraClick} cursorPointer>
                                Ещё...
                            </FRCC>
                            {user?.psychologist && (
                                <FRCC g={1} mt={2} ml={-0.4} cls={'opacity-75'} onClick={switchProfile}>
                                    <LoopRoundedIcon/>
                                    <span style={{lineHeight: '1rem', marginTop: 2}}>
                                        Я сейчас {selectedProfile === 'psychologist' ? 'психолог' : 'клиент'}
                                    </span>
                                </FRCC>
                            )}
                        </FC>
                    </FCSC>
                    <Menu
                        anchorEl={anchorElExtra}
                        open={Boolean(anchorElExtra)}
                        onClose={handleExtraClose}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}>
                        <MenuItem onClick={() => {
                            handleExtraClose();
                            handleMenuLinkClick('/gift-certificates', true);
                        }}>
                            Подарочные сертификаты
                        </MenuItem>
                        <MenuItem onClick={() => {
                            handleExtraClose();
                            handleMenuLinkClick('/gift-certificate/activate', true);
                        }}>
                            Активировать сертификат
                        </MenuItem>
                    </Menu>

                    <FC cls={'profile-section'} rounded={3} grow pos={'relative'}
                        maxH={`calc(100vh - ${headerHeight}px)`}
                        bg={plt.bg.primary} boxShadow={plt.shadow.XLO005}
                        ref={cabinetContainerRef}>
                        {selectedProfile === 'psychologist' && (
                            <Routes>
                                <Route path="conference/:consultationId" element={<ConsultationConference/>}/>
                                <Route path="consultations" element={<ConsultationList/>}/>
                                <Route path="consultations/:id" element={<>
                                    <CabinetBaseHeader text={'Консультации'} backBtn={true}/>
                                    <ConsultationDetail className={'px-3'}/>
                                </>}/>
                                <Route path="profile/*" element={<Profile selectedProfile={selectedProfile}/>}/>
                                <Route path="chats/*" element={
                                    <FC h={'100%'}>
                                        <RoomsProvider>
                                            <div className={'flex-grow-1'}>
                                                <Routes>
                                                    <Route path="/*" element={<Chat/>}>
                                                        <Route path="room/:roomId" element={<Room/>}/>
                                                    </Route>
                                                </Routes>
                                            </div>
                                        </RoomsProvider>
                                    </FC>}
                                />
                                <Route path="clients" element={<>
                                    <CabinetBaseHeader text={'Клиенты'} backBtn={false}/>
                                    <ClientsWrapper className={'px-3'}/>
                                </>}/>
                                <Route path="clients/:id" element={<>
                                    <CabinetBaseHeader text={'Клиенты'} backBtn={true}/>
                                    <ClientDetail className={'px-3'}/>
                                </>}/>
                                <Route path="work-schedule" element={<>
                                    <CabinetBaseHeader text={'График работы'} backBtn={false}/>
                                    <CalendarApp initialAppState={{
                                        initialView: "week",
                                        initialWeekStart: new Date(),
                                        initialUserType: "psychologist",
                                        initialCanEdit: true,
                                        initialAvailableViews: [],
                                        initialShowButtons: false,
                                        initialDebugging: false,
                                        initialTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                                        initialShowHorizontalLines: true,
                                        initialShowVerticalLines: true,
                                        initialShowLineToggles: false,
                                        initialMaxDaysForSelection: 0,
                                        initialShowOnlyEvents: false,
                                    }}/>
                                </>}/>
                                <Route path="payouts" element={<>
                                    <CabinetBaseHeader text={'Выплаты'} backBtn={false}/>
                                    <div className="p-3">Здесь раздел "Выплаты"</div>
                                </>}/>
                            </Routes>
                        )}

                        {selectedProfile === 'client' && (
                            <Routes>
                                <Route path="conference/:consultationId" element={<ConsultationConference/>}/>
                                <Route path="consultations" element={<ConsultationList/>}/>
                                <Route path="consultations/:id" element={<>
                                    <CabinetBaseHeader text={'Консультации'} backBtn={true}/>
                                    <ConsultationDetail className={'px-3'}/>
                                </>}
                                />
                                <Route path="profile/*" element={<Profile selectedProfile={selectedProfile}/>}/>
                                <Route path="chats/*" element={
                                    <RoomsProvider>
                                        <div className={'fc h-100'}>
                                            <div className={'flex-grow-1'}>
                                                <Routes>
                                                    <Route path="/*" element={<Chat/>}>
                                                        <Route path="room/:roomId" element={<Room/>}/>
                                                    </Route>
                                                </Routes>
                                            </div>
                                        </div>
                                    </RoomsProvider>
                                }/>
                                <Route path="courses" element={<>
                                    <CabinetBaseHeader text={'Курсы'} backBtn={false}/>
                                    <CoursesPage/>
                                </>}/>
                                <Route path="course/:productId" element={<>
                                    <CabinetBaseHeader text={''} backBtn={true}/>
                                    <CoursePage/>
                                </>}/>
                                <Route path="guides" element={<>
                                    <CabinetBaseHeader text={'Гайды'} backBtn={false}/>
                                    <GuidesPage/>
                                </>}/>
                                <Route path="guide/:productId" element={<>
                                    <CabinetBaseHeader text={''} backBtn={true}/>
                                    <GuidePage/>
                                </>}/>

                                <Route path="tariffs" element={<>
                                    <CabinetBaseHeader text={'Тарифы'} backBtn={false}/>
                                    <TariffsPage/>
                                </>}/>
                                <Route path="tariff-groups/:id" element={<>
                                    <CabinetBaseHeader text={'Тарифы'} backBtn={true}/>
                                    <TariffGroupDetailPage/>
                                </>}/>
                                <Route path="tariff/:productId" element={<>
                                    <CabinetBaseHeader text={''} backBtn={true}/>
                                    <TariffPage/>
                                </>}/>
                                <Route path="orders" element={<>
                                    <CabinetBaseHeader text={'Заказы'} backBtn={false}/>
                                    <UserOrders className={'px-3'}/>
                                </>}/>
                                <Route path="orders/:id" element={<>
                                    <CabinetBaseHeader text={'Заказы'} backBtn={true}/>
                                    <OrderDetail className={'px-3'}/>
                                </>}/>
                            </Routes>
                        )}
                        <Routes>
                            <Route path="questionnaire/*" element={<Questionnaire/>}/>
                            <Route path="psychologists" element={<Psychologists/>}/>
                            <Route path="psychologists/:id" element={<PsychologistDetailPage/>}/>
                            <Route path="psychologists/favorites" element={<FavoritePsychologists/>}/>
                            {isAuthenticated
                                ? <>
                                    <Route path="gift-certificate/activate" element={<GiftCertificateActivationPage/>}/>
                                    <Route path="gift-certificates" element={<GiftCertificatesPage/>}/>
                                    <Route path="gift-certificate/:productId" element={<GiftCertificatePage/>}/>
                                </>
                                : <>
                                    <Route path="courses" element={<CoursesPage/>}/>
                                    <Route path="course/:productId" element={<CoursePage/>}/>
                                    <Route path="guides" element={<GuidesPage/>}/>
                                    <Route path="guide/:productId" element={<GuidePage/>}/>
                                    <Route path="tariffs" element={<TariffsPage/>}/>
                                    <Route path="tariff/:productId" element={<TariffPage/>}/>
                                </>}
                            <Route path="order" element={<OrderTemplate/>}>
                                <Route path="package" element={<PackageOrder/>}/>
                                <Route path="tariff/:productId" element={<TariffOrder/>}/>
                                <Route path="course/:productId" element={<CourseOrder/>}/>
                                <Route path="guide/:productId" element={<GuideOrder/>}/>
                                <Route path="gift-certificate/:productId" element={<GiftCertificateOrder/>}/>
                                <Route index element={<Navigate to="package" replace/>}/>
                            </Route>
                        </Routes>
                    </FC>
                </FRC>
            </FC>
            <Modal isOpen={isLeaveConferenceModalOpen} onClose={cancelLeaveConference}
                   title="Подтверждение" closeBtn>
                <FC minW={'200px'} maxW={400}>
                    <p>
                        Вы собираетесь покинуть конференцию, вы уверены что хотите выйти?
                        Вы отключитесь от видео чата и не сможете видеть и слышать участников.
                    </p>
                    <FRC g={1} mt={1}>
                        <Button size={'small'} className={'pt-4px'} color={'error'} onClick={confirmLeaveConference}>
                            Выйти
                        </Button>
                        <Button size={'small'} className={'pt-4px'} color={'secondary'} onClick={cancelLeaveConference}>
                            Отмена
                        </Button>
                    </FRC>
                </FC>
            </Modal>
        </CabinetWidthContext.Provider>
    );
};

export default Cabinet;
